import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  PUSH_PROMPT_EVENT,
  addPushClickListener,
  deferPushPrePrompt,
  maybePromptForPush,
  requestPushPermission,
} from "@/hooks/useOneSignal";
import { resolvePushDestination } from "@/lib/pushRouting";
import { useAuth } from "@/hooks/useAuth";

/**
 * Native push glue that needs router context:
 *  - routes notification taps to the most specific supported destination
 *  - shows a one-time contextual explainer before the iOS system prompt
 *
 * Renders nothing on web.
 */
const PushNotifications = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const firstSessionAsked = useRef(false);

  useEffect(() => {
    return addPushClickListener((data) => {
      const dest = resolvePushDestination(data);
      // No usable metadata → open the app normally, never guess a route.
      if (dest) navigate(dest);
    });
  }, [navigate]);

  useEffect(() => {
    const onPrompt = () => setOpen(true);
    window.addEventListener(PUSH_PROMPT_EVENT, onPrompt);
    return () => window.removeEventListener(PUSH_PROMPT_EVENT, onPrompt);
  }, []);

  // First signed-in moment in the app shell (never on splash / unresolved auth).
  useEffect(() => {
    if (loading || !user || firstSessionAsked.current) return;
    firstSessionAsked.current = true;
    const t = window.setTimeout(() => maybePromptForPush(), 1500);
    return () => window.clearTimeout(t);
  }, [loading, user]);

  const handleEnable = async () => {
    setOpen(false);
    await requestPushPermission();
  };

  // "Not now" defers rather than permanently dismissing.
  const handleDismiss = () => {
    deferPushPrePrompt();
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleDismiss())}>
      <AlertDialogContent className="max-w-[340px] rounded-2xl">
        <AlertDialogHeader>
          <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
            <Bell className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <AlertDialogTitle className="text-center text-lg">
            Stay in the loop
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center text-sm leading-relaxed">
            Get notified when people follow you or react to your reviews, and when
            artists you follow announce tours or play nearby.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          <AlertDialogAction className="w-full" onClick={handleEnable}>
            Turn on notifications
          </AlertDialogAction>
          <AlertDialogCancel className="mt-0 w-full" onClick={handleDismiss}>
            Not now
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PushNotifications;
